import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    Chart as ChartJS,
    LinearScale,
    CategoryScale,
    BarElement,
    PointElement,
    LineElement,
    Legend,
    Tooltip,
    LineController,
    BarController
} from 'chart.js';
import { Chart } from 'react-chartjs-2';

ChartJS.register(LinearScale, CategoryScale, BarElement, PointElement, LineElement, Legend, Tooltip, LineController, BarController);

import PropTypes from 'prop-types';

import { Grid, MenuItem, TextField } from '@mui/material';

import Bar from '@/components/BI/graphicBar';
import GraphicProgressBar from '@/components/BI/GraphicProgressBar';
import HeaderTotalGraphic from '@/components/organisms/headers/HeaderTotalGraphic';
import useManagerPanelService from '@/services/managerPanel';
import { handleDownloadData } from '@/utils/helpers/downloads';
import { getEvaluationSectorTypeName } from '@/utils/helpers/evaluationsSectors';

import { useCheckAgro } from './hooks';
import {
    ActionCard,
    ActionCardWrapper,
    ActionsContainer,
    ActionStatus,
    ActionWrapper,
    ContainerYears,
    Div,
    DivHeader,
    GraphicWrapper,
    PaperContainer,
    PaperTitle,
    SectorsContainer,
    SectorsContent,
    SectorsDivider,
    SectorsHeader,
    SectorsWrapper,
    TextFildStyled,
    StatsWrapper,
    Section,
    FilterHeader,
    FilterTitle,
    Container
} from './styles';

const graphicOptions = {
    chartArea: { width: '80%', height: '80%' },
    colors: ['#B15FA4'],
    explorer: { axis: 'horizontal', keepInBounds: false },
    orientation: 'horizontal',
    legend: { position: 'none' },
    fontSize: 14,
    tooltip: {
        ignoreBounds: false,
        textStyle: {
            bold: true
        }
    },
    annotations: {
        textStyle: {
            fontSize: 15,
            bold: true,
            color: '#000'
        },
        boxStyle: {
            rx: 10,
            ry: 10
        }
    },
    vAxis: {
        title: 'Quant. de  Setores',
        titleTextStyle: {
            italic: false,
            color: '#9BA1A9'
        }
    }
};

export const CheckAgro = ({ idEnterprise }) => {
    const {
        selectRef,
        filterPer,
        handleFilter,
        years,
        totalPerYear,
        totalConsolidated,
        graphBarData,
        sectorsAverage,
        goToCheckAgroSectorDetails,
        countUniquePropertiesEachMonth,
        countUniquePropertiesEvaluationsEachMonth,
        percentageEachMonth
    } = useCheckAgro(idEnterprise);

    let dataAtual = new Date();
    let anoAtual = dataAtual.getFullYear();
    let mesAtual = dataAtual.getMonth();

    let countUniquePropertiesEachMonthSliced =
        years[0] == anoAtual ? countUniquePropertiesEachMonth.slice(0, mesAtual + 1) : countUniquePropertiesEachMonth;
    let countUniquePropertiesEvaluationsEachMonthSliced =
        years[0] == anoAtual ? countUniquePropertiesEvaluationsEachMonth.slice(0, mesAtual + 1) : countUniquePropertiesEvaluationsEachMonth;
    let percentageEachMonthSliced = years[0] == anoAtual ? percentageEachMonth.slice(0, mesAtual + 1) : percentageEachMonth;

    const data = {
        labels: [
            'Janeiro',
            'Fevereiro',
            'Março',
            'Abril',
            'Maio',
            'Junho',
            'Julho',
            'Agosto',
            'Setembro',
            'Outubro',
            'Novembro',
            'Dezembro'
        ],
        datasets: [
            {
                type: 'line',
                label: '% de avaliações',
                borderColor: 'rgba(41, 71, 50, 1)',
                borderWidth: 2,
                fill: false,
                data: countUniquePropertiesEvaluationsEachMonthSliced
            },
            {
                type: 'bar',
                label: 'Total de fazendas',
                backgroundColor: 'rgba(229, 202, 225, 1)',
                data: countUniquePropertiesEachMonthSliced,
                borderColor: 'white',
                borderWidth: 2
            },
            {
                type: 'bar',
                label: 'Avaliação do Check agro',
                backgroundColor: 'rgba(177, 95, 164, 1)',
                data: countUniquePropertiesEvaluationsEachMonthSliced
            }
        ]
    };

    const options = {
        maintainAspectRatio: false,
        plugins: {
            tooltip: {
                callbacks: {
                    label: (tooltipItem, data) => {
                        if (tooltipItem.datasetIndex === 0) {
                            return percentageEachMonthSliced[tooltipItem.dataIndex] + '%';
                        }
                    }
                }
            }
        }
    };

    const dispatch = useDispatch();
    const { clickedReportButton } = useSelector((state) => state.managerPanelReducer);
    const { getCheckAgroPDFReport } = useManagerPanelService();

    const handleReport = async () => {
        try {
            dispatch({ type: 'MANAGER_PANEL_REPORT_IS_LOADING', payload: true });
            const { data } = await getCheckAgroPDFReport(idEnterprise, filterPer, segment);

            if (data) {
                handleDownloadData(data, `relatorio_do_check_agro_${filterPer}`);
            }
        } catch (error) {
            console.log(error);
        } finally {
            dispatch({ type: 'MANAGER_PANEL_RESET' });
        }
    };

    useEffect(() => {
        if (clickedReportButton) {
            handleReport();
        }
    }, [clickedReportButton]);

    return (
        <Container>
            <FilterHeader>
                <FilterTitle>Visualizar por: </FilterTitle>
                <TextField
                    style={{ minWidth: '100px', backgroundColor: 'white', borderRadius: '12px' }}
                    ref={selectRef}
                    size={'small'}
                    select
                    value={filterPer}
                    onChange={(evt) => handleFilter(evt.target.value)}
                >
                    {years?.map((value, key) => (
                        <MenuItem key={key} value={value}>
                            {value}
                        </MenuItem>
                    ))}
                </TextField>
            </FilterHeader>
            <div>
                <Section>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Grid container spacing={2} gap={'20px'}>
                                <Grid item xs={12}>
                                    <StatsWrapper>
                                        <HeaderTotalGraphic
                                            title="Fazendas cadastradas"
                                            description={countUniquePropertiesEachMonth?.[countUniquePropertiesEachMonth.length - 1] ?? 0}
                                        />
                                        <HeaderTotalGraphic
                                            title="Fazendas avaliadas"
                                            description={
                                                countUniquePropertiesEvaluationsEachMonth[
                                                    countUniquePropertiesEvaluationsEachMonth.length - 1
                                                ] ?? 0
                                            }
                                        />
                                    </StatsWrapper>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Section>
            </div>
            <PaperContainer>
                {/* <HeaderTotalGraphic title="Total Consolidado:" description={`${totalConsolidated} Setores avaliados`} /> */}
                {/* <PaperContainer> */}
                <PaperTitle>Check Agro: Fazendas avaliadas / ano</PaperTitle>
                <PaperContainer>
                    <Chart type="bar" data={data} height={'400vh'} width={'100%'} options={options} />
                </PaperContainer>

                {/* <PaperTitle>Check Agro: Setores respondidos / ano</PaperTitle>
            <ContainerYears>
                <Div>
                    <TextFildStyled
                        ref={selectRef}
                        fullWidth
                        size={'small'}
                        select
                        value={filterPer}
                        onChange={(evt) => handleFilter(evt.target.value)}
                    >
                        {years?.map((value, key) => (
                            <MenuItem key={key} value={value}>
                                {value}
                            </MenuItem>
                        ))}
                    </TextFildStyled>
                </Div>
                <Div width="18rem">
                    <p>
                        <b>{filterPer} |</b> {totalPerYear} Setores respondidos
                    </p>
                </Div>
            </ContainerYears>

            <GraphicWrapper height={graphBarData.length > 0 && '400px'}>
                <Bar height="100%" width="100%" allData={graphBarData} options={graphicOptions} />
            </GraphicWrapper> */}
                {graphBarData.length > 0 && (
                    <SectorsContainer>
                        <SectorsHeader>
                            <div>Média Geral das propriedades</div>
                            <span>
                                Base {filterPer}: {totalPerYear} Setores respondidos
                            </span>
                        </SectorsHeader>
                        <ActionsContainer>
                            {sectorsAverage.map((sector) => (
                                <ActionCardWrapper key={sector.id}>
                                    <ActionCard>
                                        <div className="sector">
                                            <span className="title">{sector.name}</span>
                                            {/* <span className="type">{getEvaluationSectorTypeName(sector.type)}</span> */}
                                        </div>
                                        <span className="average">Média Geral: {Number(sector.average ?? 0).toFixed(0)}/100</span>
                                    </ActionCard>
                                    <ActionStatus className={sector.average < 50 ? 'bad' : sector.average < 60 ? 'average' : 'great'} />
                                </ActionCardWrapper>
                            ))}
                        </ActionsContainer>
                    </SectorsContainer>
                )}
            </PaperContainer>
        </Container>
    );
};

CheckAgro.propTypes = {
    idEnterprise: PropTypes.number
};
